import React, { useState, useEffect } from 'react';
import {
	BrowserRouter as Router,
	Route,
	Routes,
} from 'react-router-dom';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
	faXmark,
	faChevronLeft,
	faChevronRight,
	faChevronDown,
	faChevronUp,
	faCopy,
	faArrowDownWideShort,
	faArrowUpShortWide,
	faArrowUp,
	faPlus,
	faMinus,
	faIdCard,
	faRankingStar,
	faHeart,
	faHeartCirclePlus,
	faHeartCircleMinus,
	faUser,
	faKey,
	faFilter,
	faPaperPlane,
	faCircleQuestion,
	faScrewdriverWrench,
	faBorderAll,
	faList,
	faDownload,
	faBell,
	faTrash,
	faNewspaper,
	faBars,
	faSitemap,
	faSpinner,
	faUpRightFromSquare,
	faCheck,
	faMagnifyingGlass,
	faBug,
	faThumbsDown,
	faThumbsUp,
} from '@fortawesome/free-solid-svg-icons';

import { fetchData } from './helpers/methods';
import { CustomerContext } from './contexts/customer-context';
import { CustomerBrandingContext } from './contexts/customer-branding-context';
import { UserContext } from './contexts/user-context';
import {
	defaultPrimaryColor,
	defaultPrimaryBtnHoverColor,
	defaultPrimaryBtnTextColor,
	defaultSupportingColorA,
	defaultSupportingColorB,
	defaultSupportingColorC,
	defaultSupportingColorD,
	defaultSupportingColorE,
	defaultSupportingColorF,
	defaultSupportingColorG,
	defaultSupportingColorH,
	defaultSupportingColorI,
	defaultSupportingColorJ,
	defaultSupportingColorK,
	defaultBorderRadius,
	defaultBrandName,
	defaultLandingTitle,
	defaultLandingMessage,
} from './helpers/default-colors';

import SpinnerContainer from './component-library/spinner-container';

import Header from './views/header/header';
import Landing from './views/landing/landing';
import Catalog from './views/catalog/catalog';
import Details from './views/details/details';
import About from './views/details/about/about';
import Methods from './views/details/methods/methods';
import BundleDetails from './views/details/bundle-details/bundle-details';
import Pricing from './views/details/pricing/pricing';
import Attachments from './views/details/attachments/attachments';
import InteractiveDocumentation from './views/details/interactive-documentation/interactive-documentation';
import InteractiveDocumentationEvent from './views/details/interactive-documentation-event/interactive-documentation-event';
import ContactProvider from './views/details/contact-provider/contact-provider';
import ContactProviderExpanded from './views/details/contact-provider/contact-provider-expanded';
import ProviderUpdates from './views/details/provider-updates/provider-updates';
import ServiceNow from './views/details/servicenow/servicenow';
import RegisterForAccess from './views/details/register-for-access/register-for-access';
import ManageAccess from './views/details/manage-access/manage-access';
import TopApis from './views/catalog/top-apis/top-apis';
import Favorites from './views/catalog/favorites/favorites';
import Taxonomy from './views/catalog/taxonomy/taxonomy';
import News from './views/news/news';
import NewsAnnouncements from './views/news/announcements/announcements';
import NewsProviderUpdates from './views/news/provider-updates/provider-updates';
import Profile from './views/profile/profile';
import MyDetails from './views/profile/sub-components/my-details';
import PageNotFound from './views/page-not-found/page-not-found';
import Error from './views/error/error';
import RequestApiKey from './views/details/request-api-key/request-api-key';

library.add(
	faXmark,
	faChevronLeft,
	faChevronRight,
	faChevronDown,
	faChevronUp,
	faCopy,
	faArrowDownWideShort,
	faArrowUpShortWide,
	faArrowUp,
	faPlus,
	faMinus,
	faIdCard,
	faRankingStar,
	faHeart,
	faHeartCirclePlus,
	faHeartCircleMinus,
	faUser,
	faKey,
	faFilter,
	faPaperPlane,
	faCircleQuestion,
	faScrewdriverWrench,
	faBorderAll,
	faList,
	faDownload,
	faBell,
	faTrash,
	faNewspaper,
	faBars,
	faSitemap,
	faSpinner,
	faUpRightFromSquare,
	faCheck,
	faMagnifyingGlass,
	faBug,
	faThumbsDown,
	faThumbsUp,
);

const App = () => {
	const [loading, setLoading] = useState(true);
	const [permissionFailure, setPermissionFailure] = useState(false);
	const [error, setError] = useState(false);
	const [customerData, setCustomerData] = useState({});
	const [customerBrandingData, setCustomerBrandingData] = useState({});
	const [userData, setUserData] = useState([]);
	const defaultBranding = {
		primaryColor: defaultPrimaryColor,
		primaryBtnHoverColor: defaultPrimaryBtnHoverColor,
		primaryBtnTextColor: defaultPrimaryBtnTextColor,
		supportingColorA: defaultSupportingColorA,
		supportingColorB: defaultSupportingColorB,
		supportingColorC: defaultSupportingColorC,
		supportingColorD: defaultSupportingColorD,
		supportingColorE: defaultSupportingColorE,
		supportingColorF: defaultSupportingColorF,
		supportingColorG: defaultSupportingColorG,
		supportingColorH: defaultSupportingColorH,
		supportingColorI: defaultSupportingColorI,
		supportingColorJ: defaultSupportingColorJ,
		supportingColorK: defaultSupportingColorK,
		borderRadius: defaultBorderRadius,
		brandName: defaultBrandName,
		landingTitle: defaultLandingTitle,
		landingMessage: defaultLandingMessage,
	};

	const applyBranding = branding => {
		const {
			primaryColor,
			primaryBtnHoverColor,
			primaryBtnTextColor,
			supportingColorA,
			supportingColorB,
			supportingColorC,
			supportingColorD,
			supportingColorE,
			supportingColorF,
			supportingColorG,
			supportingColorH,
			supportingColorI,
			supportingColorJ,
			supportingColorK,
			borderRadius,
		} = branding;

		// Check for custom brand colors
		const newBorderRadius = `${borderRadius}px`;
		const root = document.documentElement;
		root?.style.setProperty('--brandable-color-primary', primaryColor);
		root?.style.setProperty('--brandable-color-primary-button-hover', primaryBtnHoverColor);
		root?.style.setProperty('--brandable-color-primary-button-text', primaryBtnTextColor);
		root?.style.setProperty('--brandable-color-supporting-a', supportingColorA);
		root?.style.setProperty('--brandable-color-supporting-b', supportingColorB);
		root?.style.setProperty('--brandable-color-supporting-c', supportingColorC);
		root?.style.setProperty('--brandable-color-supporting-d', supportingColorD);
		root?.style.setProperty('--brandable-color-supporting-e', supportingColorE);
		root?.style.setProperty('--brandable-color-supporting-f', supportingColorF);
		root?.style.setProperty('--brandable-color-supporting-g', supportingColorG);
		root?.style.setProperty('--brandable-color-supporting-h', supportingColorH);
		root?.style.setProperty('--brandable-color-supporting-i', supportingColorI);
		root?.style.setProperty('--brandable-color-supporting-j', supportingColorJ);
		root?.style.setProperty('--brandable-color-supporting-k', supportingColorK);
		root?.style.setProperty('--brandable-border-radius', newBorderRadius);
	};

	const getUserData = () => {
		setLoading(true);
		fetchData({
			uri: '/api/web/my/stuff',
			method: 'GET',
		})
			.then(({ ok, body }) => {
				if (ok) {
					const newUserData = body.whoAmI;
					setUserData(newUserData);
					setLoading(false);
				} else {
					setError(true);
					if (body.detail && body.detail === 'You do not have permission to access this.') {
						setPermissionFailure(true);
					}
					setLoading(false);
				}
			})
			.catch(() => {
				setLoading(false);
				setError(true);
			});
	};

	const getCustomerBranding = () => {
		fetchData({
			uri: '/api/web/config/branding',
			method: 'GET',
		})
			.then(({ ok, body }) => {
				if (ok) {
					const newData = body;
					let newBranding = {};
					if (newData?.primaryColor) {
						newBranding = newData;
					} else {
						newBranding = defaultBranding;
					}
					setCustomerBrandingData(newBranding);
					applyBranding(newBranding);
					getUserData();
				} else {
					const newBranding = defaultBranding;
					setCustomerBrandingData(newBranding);
					getUserData();
				}
			})
			.catch(() => {
				const newBranding = defaultBranding;
				setCustomerBrandingData(newBranding);
				getUserData();
			});
	};

	const getCustomerData = () => {
		setLoading(true);
		fetchData({
			uri: '/api/web/config',
			method: 'GET',
		})
			.then(({ ok, body }) => {
				if (ok) {
					const newCustomerData = body;
					setCustomerData(newCustomerData);
					getCustomerBranding();
				} else {
					setError(true);
					if (body.detail && body.detail === 'You do not have permission to access this.') {
						setPermissionFailure(true);
					}
					setLoading(false);
				}
			})
			.catch(() => {
				setError(true);
				setLoading(false);
			});
	};

	useEffect(() => {
		getCustomerData();
	}, []);

	return (
		<>
			{loading && (
				<SpinnerContainer />
			)}
			{!loading && !error && (
				<CustomerContext.Provider value={customerData}>
					<CustomerBrandingContext.Provider value={customerBrandingData}>
						<UserContext.Provider value={userData}>
							<Router basename="/store/">
								<Header />
								<div className="main-container" id="mainContainer">
									<div className="content-container">
										<Routes>
											<Route path="/" element={<Landing />} />
											<Route path="catalog" element={<Catalog />}>
												<Route path="top-apis" element={<TopApis />} />
												<Route path="favorites" element={<Favorites />} />
												<Route path="taxonomy" element={<Taxonomy />} />
											</Route>
											<Route path="/catalog/details/:id" element={<Details />}>
												<Route path="/catalog/details/:id/" element={<About />} />
												<Route path="methods" element={<Methods />} />
												<Route path="bundle-details" element={<BundleDetails />} />
												<Route path="pricing" element={<Pricing />} />
												<Route path="attachments" element={<Attachments />} />
												<Route path="documentation" element={<InteractiveDocumentation />} />
												<Route path="eventdoc" element={<InteractiveDocumentationEvent />} />
												<Route path="contact-provider" element={<ContactProvider />} />
												<Route path="contact-provider/:convId/:order" element={<ContactProviderExpanded />} />
												<Route path="provider-updates" element={<ProviderUpdates />} />
												<Route path="servicenow" element={<ServiceNow />} />
												<Route path="register-for-access" element={<RegisterForAccess />} />
												<Route path="request-api-key" element={<RequestApiKey />} />
												<Route path="manage-access" element={<ManageAccess />} />
											</Route>
											<Route path="/profile" element={<Profile />}>
												<Route path="/profile" element={<MyDetails />} />
											</Route>
											<Route path="news" element={<News />}>
												<Route path="announcements" element={<NewsAnnouncements />} />
												<Route path="provider-updates" element={<NewsProviderUpdates />} />
											</Route>
											<Route path="*" element={<PageNotFound />} />
										</Routes>
									</div>
								</div>
							</Router>
						</UserContext.Provider>
					</CustomerBrandingContext.Provider>
				</CustomerContext.Provider>
			)}
			{error && (
				<Error isPermissionError={permissionFailure} />
			)}
		</>
	);
};

export default App;
